import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { AssigTripsService } from 'src/app/component/assigned-trips/assigntrips-service/assigntrips-service';
import { getDistanceFromLatLon } from 'src/app/utils/utils.common';
import { PlannerHelpingService } from './planner-helping-service';

@Injectable({
  providedIn: 'root'
})
export class PlannerNearbyWiseService {

  user: any;
  preData: any;
  preFleet: any;
  selectedMapper: any;
  violation: any;
  timeCalibration: any;
  selectedDrivers: any[];
  selectedMethod: any;
  preDrivers: any;
  shiftMapper: any;

  constructor(
    private assigTripsService: AssigTripsService,
    private plannerHelpingService: PlannerHelpingService
  ) { }

  /**
   * Main entry point to plan trips with world-class Nearby Sequential VRP engine.
   * Utilizes Regret-2 Insertion, Spatio-Temporal Cost Optimization, Local Search (2-Opt & Relocate),
   * and Non-Compatible Rescue to maximize trip completion and minimize fleet deadhead.
   */
  async planTrips(user, preData, preFleet, violation, selectedMapper, selectedDrivers, selectedMethod, timeCalibration, manifestTrips, preDrivers, shiftMapper) {
    this.user = user;
    this.preData = preData;
    this.preFleet = preFleet;
    this.violation = violation;
    this.timeCalibration = timeCalibration;
    this.selectedMapper = selectedMapper;
    this.shiftMapper = shiftMapper;
    this.selectedDrivers = selectedDrivers;
    this.selectedMethod = selectedMethod;
    this.preDrivers = preDrivers;

    let planedTrips: any = {
      trips: [],
      nonCompatible: [],
      compatible: {},
      tripLogs: {},
      driversDistance: {}
    };

    const selectedTripSet = new Set<string>();
    if (this.preData.selectedTrips && Array.isArray(this.preData.selectedTrips)) {
      this.preData.selectedTrips.forEach(id => selectedTripSet.add(String(id)));
    }
    for (let trip of this.preData.trips) {
      if (selectedTripSet.has(String(trip._id))) {
        planedTrips.trips.push(trip);
      }
    }

    if (this.timeCalibration && this.timeCalibration !== "none") {
      if (this.timeCalibration === "pickup-wise") {
        await this.plannerHelpingService.applyPickupWiseCalibration(this.selectedMethod, planedTrips.trips, user);
      } else if (this.timeCalibration === "dropoff-wise") {
        await this.plannerHelpingService.applyDropoffWiseCalibration(this.selectedMethod, planedTrips.trips, user);
      } else {
        await this.plannerHelpingService.applyMemberWiseHybridCalibration(this.selectedMethod, planedTrips.trips, user);
      }
    }

    this.selectedDrivers.sort((a, b) =>
      (a.displayName || '').localeCompare(b.displayName || '')
    );

    planedTrips.trips.sort((a, b) => {
      const aTime = a.scheduleTime ? new Date(a.scheduleTime).getTime() : 0;
      const bTime = b.scheduleTime ? new Date(b.scheduleTime).getTime() : 0;
      return aTime - bTime;
    });

    // Populate pre-manifest trips if any
    if (manifestTrips && manifestTrips.length) {
      for (let item of manifestTrips) {
        if (this.selectedDrivers.some(driver => driver._id === item.driver)) {
          if (item.driver) {
            if (!planedTrips.compatible[item.driver]) {
              planedTrips.compatible[item.driver] = {
                driver: preDrivers[item.driver],
                trips: []
              };
            }
            if (item) {
              item.preTrip = true;
              planedTrips.compatible[item.driver].trips.push(item);
            }
          }
        }
      }
      // Initialize executed stops for pre-manifest assignments
      for (const driverId in planedTrips.compatible) {
        const driverData = planedTrips.compatible[driverId];
        if (driverData.trips && driverData.trips.length > 0 && !driverData.executedStops) {
          await this.initializeExecutedStopsForTrips(driverData.driver, driverData);
        }
      }
    }

    await this.assignTripsToDrivers(planedTrips, this.selectedDrivers);
    await this.finalizeSequences(planedTrips);

    // reset trips array after planning
    planedTrips.trips = [];

    // Final Output Logs
    for (const driverId in planedTrips.compatible) {
      const data = planedTrips.compatible[driverId];
      if (data.driver) {
        console.log(`[Nearby VRP] Driver: ${data.driver.displayName} (${data.trips.length} trips, ${(planedTrips.driversDistance[driverId] || 0).toFixed(1)} miles)`);
        console.log(`[Nearby VRP] Trips IDs: ${data.trips.map(t => t.tripId).join(', ')}`);
        if (data.stops) {
          console.log(`[Nearby VRP] Stops:`, data.stops.map(s => `${s.priority}. [${s.stopType}] Patient: ${s.personName || 'Unknown'} (${s.tripId}) ETA: ${s.estimatedArrivalTime}`));
        }
      }
    }

    return planedTrips;
  }

  /**
   * Initializes executed stops and baseline metrics for pre-assigned manifest trips.
   */
  async initializeExecutedStopsForTrips(driver: any, driverData: any) {
    const sim = await this.simulateRouteFeasibility(driver, driverData.trips);
    if (sim.isFeasible) {
      driverData.executedStops = sim.stops;
    }
  }

  /**
   * Formats executed stops into user-facing stops array and attaches pickup/dropoff orders.
   */
  async finalizeSequences(planedTrips) {
    for (const driverId in planedTrips.compatible) {
      const driverData = planedTrips.compatible[driverId];
      if (driverData.executedStops) {
        driverData.stops = driverData.executedStops.map((stop, index) => {
          const stopObj = {
            ...stop,
            priority: index + 1,
            latitude: stop.lat,
            longitude: stop.lon,
            address: stop.address || '',
            contactNumber: stop.contactNumber || '',
            passengersOnBoard: 1,
            personName: stop.priorityClient ? stop.priorityClient.displayName : '',
            sortType: stop.type === 'P' ? 'pickup' : 'dropoff',
            stopType: stop.type === 'P' ? 'pickup' : 'dropoff',
            scheduledTime: stop.scheduleTime,
            appointmentTime: stop.appointmentTime,
            estimatedArrivalTime: stop.estimatedArrivalTime,
            isGrouped: false,
            tripId: stop.tripId,
            _id: stop._id
          };
          return stopObj;
        });

        driverData.trips.forEach(trip => {
          const pickupStop = driverData.stops.find(stop => stop._id === trip._id && stop.stopType === 'pickup');
          const dropoffStop = driverData.stops.find(stop => stop._id === trip._id && stop.stopType === 'dropoff');
          trip.pickupOrder = pickupStop ? pickupStop.priority : null;
          trip.dropOffOrder = dropoffStop ? dropoffStop.priority : null;
          trip.priority = trip.pickupOrder;
          trip.isGrouped = false;
          trip.routeOrderLabel = (trip.pickupOrder && trip.dropOffOrder)
            ? `PU #${trip.pickupOrder} → DO #${trip.dropOffOrder}`
            : '';
        });

        this.validateAndLogAppointmentTimes(driverData, driverId);
      }
    }
  }

  /**
   * Validates appointment time compliance and logs audit metrics.
   */
  private validateAndLogAppointmentTimes(driverData, driverId) {
    try {
      const appointmentBuffer = 5;
      const appointmentMetrics = {
        total: 0,
        onTime: 0,
        late: 0,
        tooEarly: 0,
        warnings: []
      };

      if (!driverData.stops) return;

      driverData.stops.forEach((stop) => {
        if (stop.stopType === 'dropoff') {
          appointmentMetrics.total++;

          const appointmentTime = moment(stop.appointmentTime).tz(this.user.timeZone);
          const estimatedArrival = moment(stop.estimatedArrivalTime).tz(this.user.timeZone);

          const minutesEarly = appointmentTime.diff(estimatedArrival, 'minutes');
          const minutesLate = estimatedArrival.diff(appointmentTime, 'minutes');

          if (minutesEarly >= 0 && minutesEarly <= appointmentBuffer) {
            appointmentMetrics.onTime++;
            console.log(
              `✓ ON-TIME [Nearby]: ${stop.personName} - Appointment ${appointmentTime.format('HH:mm')}, ` +
              `Arriving ${minutesEarly}min early (within buffer)`
            );
          } else if (minutesEarly > appointmentBuffer) {
            appointmentMetrics.tooEarly++;
          } else if (minutesLate > 0) {
            appointmentMetrics.late++;
            console.error(
              `✗ LATE [Nearby]: ${stop.personName} - Appointment ${appointmentTime.format('HH:mm')}, ` +
              `Arriving ${minutesLate}min LATE`
            );
          }
        }
      });

      const onTimePercentage = appointmentMetrics.total > 0
        ? ((appointmentMetrics.onTime / appointmentMetrics.total) * 100).toFixed(1)
        : 0;

      console.log(
        `\n📊 [Nearby VRP] APPOINTMENT SUMMARY - Driver: ${driverData.driver ? driverData.driver.displayName : driverId}\n` +
        `  Total Appointments: ${appointmentMetrics.total}\n` +
        `  ✓ On-Time: ${appointmentMetrics.onTime} (${onTimePercentage}%)\n` +
        `  ✗ Late: ${appointmentMetrics.late}\n`
      );

    } catch (error) {
      console.error('Error validating appointment times:', error);
    }
  }

  /**
   * World-Class Regret-2 VRP Insertion Engine:
   * Instead of a greedy chronological loop, at each step it calculates the "Regret" of delaying each unassigned trip:
   * Regret = Cost(2nd best driver) - Cost(1st best driver).
   * Trips that have high regret (or only 1 feasible driver) are assigned first to prevent infeasibilities.
   */
  async assignTripsToDrivers(planedTrips, drivers) {
    const unassigned = [...planedTrips.trips];
    const clientDriverMap: any = {};

    while (unassigned.length > 0) {
      let bestCandidate: any = null;
      let highestRegret = -Infinity;
      let bestTripIndex = -1;

      // Evaluate each unassigned trip across all eligible drivers
      for (let tIdx = 0; tIdx < unassigned.length; tIdx++) {
        const trip = unassigned[tIdx];
        const clientId = (trip.priorityClient && trip.priorityClient.displayName)
          ? trip.priorityClient.displayName.toLowerCase()
          : (trip.client ? String(trip.client).toLowerCase() : '');

        // Determine candidate drivers
        let candidateDrivers = [];
        if (this.violation && this.violation.prefferedToSameDriver && clientId && clientDriverMap[clientId]) {
          const prefDriver = drivers.find(d => d._id === clientDriverMap[clientId]);
          if (prefDriver && this.isTripEligibleForDriver(planedTrips, trip, prefDriver)) {
            candidateDrivers.push(prefDriver);
          }
        }
        if (candidateDrivers.length === 0) {
          candidateDrivers = drivers.filter(d => this.isTripEligibleForDriver(planedTrips, trip, d));
        }

        // Evaluate all feasible insertions for this trip across candidate drivers
        const driverEvaluations: any[] = [];
        for (const driver of candidateDrivers) {
          const evalResult = await this.evaluateDriverInsertions(planedTrips, driver, trip, clientDriverMap[clientId]);
          if (evalResult && evalResult.isFeasible) {
            driverEvaluations.push({
              driver,
              ...evalResult
            });
          }
        }

        if (driverEvaluations.length === 0) {
          continue;
        }

        // Sort driver options by score ascending (lowest cost first)
        driverEvaluations.sort((a, b) => a.score - b.score);

        const bestOption = driverEvaluations[0];
        let regret = 0;
        if (driverEvaluations.length === 1) {
          // Only 1 driver feasible: high urgency regret
          regret = 10000 - bestOption.score;
        } else {
          // Regret: difference between second best and best option
          regret = driverEvaluations[1].score - bestOption.score;
        }

        // If preferred driver match, prioritize immediate assignment
        if (this.violation && this.violation.prefferedToSameDriver && clientId && clientDriverMap[clientId] === bestOption.driver._id) {
          regret += 5000;
        }

        if (regret > highestRegret) {
          highestRegret = regret;
          bestCandidate = {
            trip,
            tripIndex: tIdx,
            clientId,
            driverOption: bestOption
          };
        }
      }

      if (!bestCandidate) {
        // No remaining unassigned trip can be feasibly scheduled
        break;
      }

      // Assign the best candidate trip to its best driver
      const { trip, tripIndex, clientId, driverOption } = bestCandidate;
      const driverId = driverOption.driver._id;

      if (!planedTrips.compatible[driverId]) {
        planedTrips.compatible[driverId] = {
          driver: driverOption.driver,
          trips: [],
          executedStops: []
        };
      }

      const driverAssignment = planedTrips.compatible[driverId];
      const insertIdx = driverOption.insertIndex !== undefined && driverOption.insertIndex !== null
        ? driverOption.insertIndex
        : driverAssignment.trips.length;

      driverAssignment.trips.splice(insertIdx, 0, trip);
      driverAssignment.executedStops = driverOption.stops;

      planedTrips.driversDistance[driverId] = driverOption.totalDistance;
      if (clientId) {
        clientDriverMap[clientId] = driverId;
      }

      // Remove from unassigned
      unassigned.splice(tripIndex, 1);
    }

    // Move any remaining unassigned trips to nonCompatible
    for (const trip of unassigned) {
      planedTrips.nonCompatible.push(trip);
      console.warn(`[Nearby VRP] Trip ${trip.tripId} (${trip.priorityClient ? trip.priorityClient.displayName : 'Unknown'}) could not be scheduled.`);
    }

    // Run Post-Optimization Local Search (2-Opt and Inter-Route Relocate)
    await this.optimizeRoutesLocalSearch(planedTrips, drivers);

    // Rescue pass for non-compatible trips after routes are settled
    if (planedTrips.nonCompatible.length > 0) {
      await this.rescueNonCompatibleTrips(planedTrips, drivers);
    }

    return planedTrips;
  }

  /**
   * Post-Optimization Local Search:
   * 1. Intra-route 2-Opt: Swaps adjacent trips within a driver's route if it decreases deadhead distance.
   * 2. Inter-route Relocate: Moves a trip from an overloaded driver to a nearby idle driver if it reduces total miles.
   */
  async optimizeRoutesLocalSearch(planedTrips, drivers) {
    const driverIds = Object.keys(planedTrips.compatible);

    // 1. Intra-route 2-Opt
    for (const driverId of driverIds) {
      const driverData = planedTrips.compatible[driverId];
      if (!driverData.trips || driverData.trips.length <= 1) continue;

      let improved = true;
      let iteration = 0;
      while (improved && iteration < 3) {
        improved = false;
        iteration++;

        for (let i = 0; i < driverData.trips.length - 1; i++) {
          const candidateTrips = [...driverData.trips];
          // Swap adjacent trips
          const temp = candidateTrips[i];
          candidateTrips[i] = candidateTrips[i + 1];
          candidateTrips[i + 1] = temp;

          const sim = await this.simulateRouteFeasibility(driverData.driver, candidateTrips);
          if (sim.isFeasible && sim.totalDistance < (planedTrips.driversDistance[driverId] - 0.5)) {
            driverData.trips = candidateTrips;
            driverData.executedStops = sim.stops;
            planedTrips.driversDistance[driverId] = sim.totalDistance;
            improved = true;
            break;
          }
        }
      }
    }

    // 2. Inter-route Relocate (move trip to another driver if fleet miles decrease)
    if (driverIds.length >= 2) {
      for (const srcDriverId of driverIds) {
        const srcData = planedTrips.compatible[srcDriverId];
        if (!srcData.trips || srcData.trips.length <= 1) continue;

        for (let tIdx = srcData.trips.length - 1; tIdx >= 0; tIdx--) {
          const tripToMove = srcData.trips[tIdx];
          if (tripToMove.preTrip) continue;

          for (const dstDriverId of driverIds) {
            if (dstDriverId === srcDriverId) continue;
            const dstData = planedTrips.compatible[dstDriverId];
            if (!this.isTripEligibleForDriver(planedTrips, tripToMove, dstData.driver)) continue;

            const dstEval = await this.evaluateDriverInsertions(planedTrips, dstData.driver, tripToMove, null);
            if (dstEval && dstEval.isFeasible) {
              const srcCandidateTrips = srcData.trips.filter((_, idx) => idx !== tIdx);
              const srcSim = await this.simulateRouteFeasibility(srcData.driver, srcCandidateTrips);
              if (srcSim.isFeasible) {
                const oldTotalDist = (planedTrips.driversDistance[srcDriverId] || 0) + (planedTrips.driversDistance[dstDriverId] || 0);
                const newTotalDist = srcSim.totalDistance + dstEval.totalDistance;

                if (newTotalDist < (oldTotalDist - 1.0)) {
                  // Beneficial move!
                  srcData.trips = srcCandidateTrips;
                  srcData.executedStops = srcSim.stops;
                  planedTrips.driversDistance[srcDriverId] = srcSim.totalDistance;

                  dstData.trips.splice(dstEval.insertIndex, 0, tripToMove);
                  dstData.executedStops = dstEval.stops;
                  planedTrips.driversDistance[dstDriverId] = dstEval.totalDistance;
                  break;
                }
              }
            }
          }
        }
      }
    }
  }

  /**
   * Rescue Pass: Re-evaluates unassigned trips from nonCompatible after routes are fully formed.
   */
  async rescueNonCompatibleTrips(planedTrips, drivers) {
    if (!planedTrips.nonCompatible || planedTrips.nonCompatible.length === 0) return;

    for (let i = planedTrips.nonCompatible.length - 1; i >= 0; i--) {
      const trip = planedTrips.nonCompatible[i];
      let bestChoice: any = null;
      let minScore = Infinity;

      for (const driver of drivers) {
        if (!this.isTripEligibleForDriver(planedTrips, trip, driver)) continue;
        const evalResult = await this.evaluateDriverInsertions(planedTrips, driver, trip, null);
        if (evalResult && evalResult.isFeasible && evalResult.score < minScore) {
          minScore = evalResult.score;
          bestChoice = { driver, ...evalResult };
        }
      }

      if (bestChoice) {
        const driverId = bestChoice.driver._id;
        if (!planedTrips.compatible[driverId]) {
          planedTrips.compatible[driverId] = {
            driver: bestChoice.driver,
            trips: [],
            executedStops: []
          };
        }

        const driverAssignment = planedTrips.compatible[driverId];
        driverAssignment.trips.splice(bestChoice.insertIndex, 0, trip);
        driverAssignment.executedStops = bestChoice.stops;
        planedTrips.driversDistance[driverId] = bestChoice.totalDistance;

        planedTrips.nonCompatible.splice(i, 1);
        console.log(`[Nearby VRP] Rescued Trip ${trip.tripId} into Driver ${bestChoice.driver.displayName}!`);
      }
    }
  }

  /**
   * Checks static driver eligibility (shift, funding source, service area, space type, gender).
   */
  isTripEligibleForDriver(planedTrips, selectedTrip, driver) {
    let isCompatible = false;
    let logs: any = {};

    const isFundingSourceWiseExist = this.fundingSourcesWiseTrip(selectedTrip, driver);
    logs.isFundingSourceWiseExist = isFundingSourceWiseExist;

    if (isFundingSourceWiseExist) {
      const isDriverShiftWiseExist = this.checkDriverShift(selectedTrip, driver);
      logs.isDriverShiftWiseExist = isDriverShiftWiseExist;

      if (isDriverShiftWiseExist) {
        const isServiceAreasWiseExist = this.serviceAreasWiseTrip(selectedTrip, driver);
        logs.isServiceAreasWiseExist = isServiceAreasWiseExist;

        if (isServiceAreasWiseExist) {
          const isSpaceTypeWiseExist = this.spaceTypeWiseTrip(selectedTrip, driver);
          logs.isSpaceTypeWiseExist = isSpaceTypeWiseExist;

          if (isSpaceTypeWiseExist) {
            const isGenderWiseExist = this.genderWiseTrip(selectedTrip, driver);
            logs.isGenderWiseExist = isGenderWiseExist;

            if (isGenderWiseExist) {
              if (!planedTrips.driversDistance[driver._id]) planedTrips.driversDistance[driver._id] = 0;
              const isMaxFactor = this.checkTripMaxFactor(planedTrips, driver, planedTrips.driversDistance[driver._id]);
              logs.isMaxFactor = isMaxFactor;

              if (isMaxFactor) {
                isCompatible = true;
              }
            }
          }
        }
      }
    }

    if (!isCompatible) {
      if (planedTrips.tripLogs[driver._id]) {
        planedTrips.tripLogs[driver._id].reasons.push({ trip: selectedTrip, logs });
      } else {
        planedTrips.tripLogs[driver._id] = { driver, reasons: [{ trip: selectedTrip, logs }] };
      }
    }

    return isCompatible;
  }

  checkDriverShift(selectedTrip, driver) {
    let isExist = false;
    const start = moment(this.preData.date).tz(this.user.timeZone).startOf('day');
    const end = moment(this.preData.date).tz(this.user.timeZone).endOf('day');

    if (this.shiftMapper[driver._id] && this.shiftMapper[driver._id].shifts && this.shiftMapper[driver._id].shifts.length) {
      const checkTodayShift = this.shiftMapper[driver._id].shifts.find(item => item.day == start.day() && item.isEnabled);
      if (checkTodayShift && checkTodayShift.checkin && checkTodayShift.checkout) {
        start.set({ hour: Number(checkTodayShift.checkin.split(':')[0]), minute: Number(checkTodayShift.checkin.split(':')[1]) });
        end.set({ hour: Number(checkTodayShift.checkout.split(':')[0]), minute: Number(checkTodayShift.checkout.split(':')[1]) });

        const tripStart = moment(selectedTrip.scheduleTime).tz(this.user.timeZone);
        const tripEnd = moment(selectedTrip.appointmentTime).tz(this.user.timeZone);

        if (tripStart.isBetween(start, end, null, '[]') && tripEnd.isBetween(start, end, null, '[]')) {
          isExist = true;
        }
      }
    }
    return isExist;
  }

  checkTripMaxFactor(planedTrips, driver, totalDistance) {
    let isExecute = true;
    if (this.violation) {
      let numberOfTrips = true;
      if (this.violation.maxPerRun) {
        if (planedTrips.compatible[driver._id] && planedTrips.compatible[driver._id].trips) {
          if (planedTrips.compatible[driver._id].trips.length >= this.violation.maxPerRun) {
            numberOfTrips = false;
          }
        }
      }

      let numberOfTripsMiles = true;
      if (this.violation.maxDistancePerRun) {
        if (totalDistance >= this.violation.maxDistancePerRun) {
          numberOfTripsMiles = false;
        }
      }

      isExecute = (numberOfTrips && numberOfTripsMiles);
    }
    return isExecute;
  }

  fundingSourcesWiseTrip(selectedTrip, driver) {
    let isExist = false;
    const isfundingSource = driver.fundingSources && driver.fundingSources.some(d =>
      d.name === selectedTrip.cooperate &&
      moment(this.preData.date).isBetween(
        moment(d.validityRange[0]).tz(this.user.timeZone).subtract(1, 'day'),
        moment(d.validityRange[1]).tz(this.user.timeZone).add(1, 'day')
      )
    );

    if (selectedTrip.cooperate && isfundingSource) {
      const driverFleetId = this.selectedMapper ? this.selectedMapper.mappers[driver._id] : null;
      if (driverFleetId && this.preFleet[driverFleetId] && this.preFleet[driverFleetId].fundingSources) {
        if (this.preFleet[driverFleetId].fundingSources.includes(selectedTrip.cooperate)) {
          isExist = true;
        }
      }
    }
    return isExist;
  }

  serviceAreasWiseTrip(selectedTrip, driver) {
    let isExist = false;
    if (selectedTrip.serviceArea && this.shiftMapper[driver._id] && this.shiftMapper[driver._id].serviceAreas) {
      if (this.shiftMapper[driver._id].serviceAreas.includes(selectedTrip.serviceArea)) {
        isExist = true;
      }
    }
    return isExist;
  }

  spaceTypeWiseTrip(selectedTrip, driver) {
    let isExist = false;
    if (this.violation && !this.violation.isCapacityViolation) {
      const driverFleetId = this.selectedMapper ? this.selectedMapper.mappers[driver._id] : null;
      if (driverFleetId && selectedTrip.spaceType && this.preFleet[driverFleetId] && this.preFleet[driverFleetId].capacities) {
        isExist = this.preFleet[driverFleetId].capacities.some(capacity => capacity.spaceType === selectedTrip.spaceType);
      }
    } else {
      isExist = true;
    }
    return isExist;
  }

  genderWiseTrip(selectedTrip, driver) {
    let isExist = true;
    if (selectedTrip.priorityClient && selectedTrip.priorityClient.isPrefferedFemale) {
      isExist = driver.gender && driver.gender.toLowerCase() === 'female';
    }
    if (selectedTrip.preferredDriver) {
      isExist = driver._id === selectedTrip.preferredDriver;
    } else if (selectedTrip.priorityClient && selectedTrip.priorityClient.preferredDriver) {
      isExist = driver._id === selectedTrip.priorityClient.preferredDriver;
    }
    return isExist;
  }

  /**
   * Builds the [Pickup, Dropoff] stop pair for a given trip.
   */
  private buildStopsForTrip(trip: any): any[] {
    const contact = (trip.priorityClient && trip.priorityClient.contactNumber) ? trip.priorityClient.contactNumber : '';
    const pickupStop = {
      _id: trip._id,
      tripId: trip.tripId,
      type: 'P',
      time: moment(trip.scheduleTime).tz(this.user.timeZone).valueOf(),
      lat: trip.jobOriginLatitude,
      lon: trip.jobOriginLongitude,
      address: trip.jobOriginAddress || '',
      contactNumber: contact,
      spaceType: trip.spaceType,
      priorityClient: trip.priorityClient,
      scheduleTime: trip.scheduleTime,
      appointmentTime: trip.appointmentTime,
      pickupLat: trip.jobOriginLatitude,
      pickupLon: trip.jobOriginLongitude,
      dropoffLat: trip.jobDestinationLatitude,
      dropoffLon: trip.jobDestinationLongitude
    };

    const dropoffStop = {
      _id: trip._id,
      tripId: trip.tripId,
      type: 'D',
      time: moment(trip.appointmentTime).tz(this.user.timeZone).valueOf(),
      lat: trip.jobDestinationLatitude,
      lon: trip.jobDestinationLongitude,
      address: trip.jobDestinationAddress || '',
      contactNumber: contact,
      spaceType: trip.spaceType,
      priorityClient: trip.priorityClient,
      scheduleTime: trip.scheduleTime,
      appointmentTime: trip.appointmentTime,
      pickupLat: trip.jobOriginLatitude,
      pickupLon: trip.jobOriginLongitude,
      dropoffLat: trip.jobDestinationLatitude,
      dropoffLon: trip.jobDestinationLongitude
    };

    return [pickupStop, dropoffStop];
  }

  /**
   * Evaluates inserting a trip into a driver's route at all valid positions (Before, Between, After).
   * Calculates Spatio-Temporal Cost:
   * Cost = AddedDeadhead + 0.15*Duration + 0.05*IdleWaitTime + 0.3*TripCount - PreferredBonus
   */
  async evaluateDriverInsertions(planedTrips, driver, trip, preferredDriverId): Promise<any> {
    const assignedTrips: any[] = (planedTrips.compatible[driver._id] && planedTrips.compatible[driver._id].trips)
      ? planedTrips.compatible[driver._id].trips
      : [];

    // Case 1: Driver has 0 trips -> First Trip Possibility
    if (assignedTrips.length === 0) {
      const candidate = [trip];
      const sim = await this.simulateRouteFeasibility(driver, candidate);
      if (sim.isFeasible) {
        let score = sim.deadheadDistance + (0.15 * sim.totalDuration);
        if (preferredDriverId === driver._id) {
          score -= 25; // Preferred driver / return trip bonus
        }
        return {
          isFeasible: true,
          insertIndex: 0,
          stops: sim.stops,
          totalDistance: sim.totalDistance,
          totalDuration: sim.totalDuration,
          deadheadDistance: sim.deadheadDistance,
          score: score
        };
      }
      return { isFeasible: false, reason: sim.reason };
    }

    const feasibleOptions: any[] = [];
    const N = assignedTrips.length;
    const newTripStart = moment(trip.scheduleTime).tz(this.user.timeZone).valueOf();

    for (let k = 0; k <= N; k++) {
      // Temporal pruning
      if (k === 0) {
        const firstTripStart = moment(assignedTrips[0].scheduleTime).tz(this.user.timeZone).valueOf();
        if (newTripStart > firstTripStart) continue;
      } else if (k === N) {
        const lastTripStart = moment(assignedTrips[N - 1].scheduleTime).tz(this.user.timeZone).valueOf();
        if (newTripStart < lastTripStart) continue;
      } else {
        const prevStart = moment(assignedTrips[k - 1].scheduleTime).tz(this.user.timeZone).valueOf();
        const nextStart = moment(assignedTrips[k].scheduleTime).tz(this.user.timeZone).valueOf();
        if (newTripStart < prevStart || newTripStart > nextStart) continue;
      }

      const candidate: any[] = [];
      for (let i = 0; i < k; i++) candidate.push(assignedTrips[i]);
      candidate.push(trip);
      for (let i = k; i < N; i++) candidate.push(assignedTrips[i]);

      const sim = await this.simulateRouteFeasibility(driver, candidate);
      if (sim.isFeasible) {
        // Spatio-Temporal Cost Function:
        // Prioritizes minimal deadhead miles + smooth transition time + balanced workload
        let score = sim.deadheadDistance + (0.15 * sim.totalDuration) + (0.3 * assignedTrips.length);

        // Soft penalty for excessive idle waiting
        if (sim.idleMinutes && sim.idleMinutes > 30) {
          score += 0.05 * (sim.idleMinutes - 30);
        }

        // Strong bonus if driver is client's preferred driver or same driver for round trip
        if (preferredDriverId === driver._id) {
          score -= 25;
        }

        feasibleOptions.push({
          isFeasible: true,
          insertIndex: k,
          stops: sim.stops,
          totalDistance: sim.totalDistance,
          totalDuration: sim.totalDuration,
          deadheadDistance: sim.deadheadDistance,
          score: score
        });
      }
    }

    if (feasibleOptions.length === 0) {
      return { isFeasible: false, reason: 'No feasible sequential slot meets time and shift constraints' };
    }

    feasibleOptions.sort((a, b) => a.score - b.score);
    return feasibleOptions[0];
  }

  /**
   * Chronological Route Simulation Engine for Sequential Solo Trips:
   * Validates:
   * 1. Shift check-in and checkout times.
   * 2. Break cushions between consecutive trips.
   * 3. Accurate service dwell times per spaceType.
   * 4. Strict dropoff appointment compliance (ETA <= appointmentTime + 5 min).
   * 5. Pickup arrival window (15-min early snap, relaxationMargin).
   */
  async simulateRouteFeasibility(driver: any, trips: any[]): Promise<any> {
    if (!trips || trips.length === 0) {
      return { isFeasible: false, reason: 'Empty trip list' };
    }

    const startOfDay = moment(this.preData.date).tz(this.user.timeZone).startOf('day');
    const todayShift = this.shiftMapper && this.shiftMapper[driver._id] && this.shiftMapper[driver._id].shifts
      ? this.shiftMapper[driver._id].shifts.find(s => s.day === startOfDay.day() && s.isEnabled)
      : null;

    if (!todayShift || !todayShift.checkin || !todayShift.checkout) {
      return { isFeasible: false, reason: 'Driver shift not found' };
    }

    let currentTime = moment(startOfDay).set({
      hour: Number(todayShift.checkin.split(':')[0]),
      minute: Number(todayShift.checkin.split(':')[1])
    });

    let checkoutTime = moment(startOfDay).set({
      hour: Number(todayShift.checkout.split(':')[0]),
      minute: Number(todayShift.checkout.split(':')[1])
    });

    let currentLat = driver.startingPointAddress ? driver.startingPointAddress.latitude : null;
    let currentLon = driver.startingPointAddress ? driver.startingPointAddress.longitude : null;

    if (currentLat === null || currentLon === null) {
      return { isFeasible: false, reason: 'Driver has no starting point coordinates' };
    }

    const relaxationMargin = (this.violation && this.violation.groupCushion) ? this.violation.groupCushion : 15;
    const breakCushion = (this.violation && this.violation.breakCushion) ? this.violation.breakCushion : 5;

    let totalDistance = 0;
    let totalDuration = 0;
    let totalDeadheadDistance = 0;
    let totalIdleMinutes = 0;
    const executedStops: any[] = [];

    for (let i = 0; i < trips.length; i++) {
      const trip = trips[i];
      const stopPair = this.buildStopsForTrip(trip);
      const pStop = stopPair[0];
      const dStop = stopPair[1];

      // Add break cushion between consecutive trips
      if (i > 0) {
        currentTime.add(breakCushion, 'minutes');
      }

      // Leg 1: Deadhead from current location (start base or previous dropoff) to pickup
      const deadhead = await this.calculateDistance(currentLat, currentLon, pStop.lat, pStop.lon);
      const deadheadDist = deadhead && deadhead.distance ? deadhead.distance : 0;
      const deadheadDur = deadhead && deadhead.duration ? deadhead.duration : 0;

      totalDistance += deadheadDist;
      totalDuration += deadheadDur;
      totalDeadheadDistance += deadheadDist;
      currentTime.add(deadheadDur, 'minutes');

      // Pickup Time Window check
      const pTargetTime = moment(pStop.time).tz(this.user.timeZone);
      const earliestWindow = pTargetTime.clone().subtract(15, 'minutes');

      const idleTime = pTargetTime.diff(currentTime, 'minutes');
      if (idleTime > 20) {
        totalIdleMinutes += idleTime;
        currentTime = pTargetTime.clone();
      } else if (currentTime.isBefore(earliestWindow)) {
        totalIdleMinutes += earliestWindow.diff(currentTime, 'minutes');
        currentTime = earliestWindow.clone();
      }

      pStop.estimatedArrivalTime = currentTime.toISOString();

      if (this.violation && !this.violation.isTimeViolation) {
        if (currentTime.isAfter(pTargetTime.clone().add(relaxationMargin, 'minutes'))) {
          return { isFeasible: false, reason: `Pickup arrival exceeded relaxation margin for ${trip.tripId}` };
        }
      }

      // Load Service Time
      let loadMinutes = 3;
      const stData = this.preData.spaceTypes ? this.preData.spaceTypes[pStop.spaceType] : null;
      if (stData && stData.loadTime && stData.loadTime.includes(':')) {
        const parts = stData.loadTime.split(':').map(Number);
        loadMinutes = (parts[0] * 60) + parts[1];
      }
      currentTime.add(loadMinutes, 'minutes');

      if (currentTime.isBefore(pTargetTime)) {
        currentTime = pTargetTime.clone();
      }

      // Leg 2: Direct Trip Leg from Pickup to Dropoff
      const tripLeg = await this.calculateDistance(pStop.lat, pStop.lon, dStop.lat, dStop.lon);
      const tripDist = tripLeg && tripLeg.distance ? tripLeg.distance : 0;
      const tripDur = tripLeg && tripLeg.duration ? tripLeg.duration : 0;

      totalDistance += tripDist;
      totalDuration += tripDur;
      currentTime.add(tripDur, 'minutes');

      dStop.estimatedArrivalTime = currentTime.toISOString();

      // Dropoff Appointment Constraint (Strict: on-time guarantee)
      const dTargetTime = moment(dStop.time).tz(this.user.timeZone);
      const appointmentBuffer = 5; // 5 min permissible buffer
      const maxAllowedArrival = dTargetTime.clone().add(appointmentBuffer, 'minutes');

      if (currentTime.isAfter(maxAllowedArrival)) {
        return {
          isFeasible: false,
          reason: `Late dropoff for ${trip.tripId}: arrives ${currentTime.format('HH:mm')}, appointment was ${dTargetTime.format('HH:mm')}`
        };
      }

      // Unload Service Time
      let unloadMinutes = 3;
      if (stData && stData.unloadTime && stData.unloadTime.includes(':')) {
        const parts = stData.unloadTime.split(':').map(Number);
        unloadMinutes = (parts[0] * 60) + parts[1];
      }
      currentTime.add(unloadMinutes, 'minutes');

      currentLat = dStop.lat;
      currentLon = dStop.lon;
      executedStops.push(pStop, dStop);
    }

    // Driver Shift Checkout limit check
    if (currentTime.isAfter(checkoutTime.clone().add(relaxationMargin, 'minutes'))) {
      return { isFeasible: false, reason: `Route exceeds driver shift checkout (${currentTime.format('HH:mm')} > ${checkoutTime.format('HH:mm')})` };
    }

    return {
      isFeasible: true,
      stops: executedStops.map((s, idx) => ({ ...s, priority: idx + 1 })),
      totalDistance: Math.round(totalDistance * 10) / 10,
      totalDuration: Math.round(totalDuration),
      deadheadDistance: Math.round(totalDeadheadDistance * 10) / 10,
      idleMinutes: totalIdleMinutes
    };
  }

  async calculateDistance(lat1, lon1, lat2, lon2) {
    return await this.assigTripsService.getDistance(lat1, lon1, lat2, lon2, this.selectedMethod);
  }
}
